import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, AlertCircle, Download, FileText } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { profitCalculationService } from '../../services/profitCalculationService';
import AdminHeader from '../admin/components/AdminHeader';
import MetricCard from '../admin-dashboard/components/MetricCard';
import ProfitTrendChart from './components/ProfitTrendChart';
import TopProjectsTable from './components/TopProjectsTable';
import PerformanceMetrics from './components/PerformanceMetrics';
import FilterControls from './components/FilterControls';
import ExportPanel from './components/ExportPanel';

const ReportsAndAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const { user, profile, signOut, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [calculations, setCalculations] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [dateRange, setDateRange] = useState('year');
  const [clientFilter, setClientFilter] = useState('all');
  const [projectTypeFilter, setProjectTypeFilter] = useState('all');
  const [showExportPanel, setShowExportPanel] = useState(false);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (profile?.role !== 'admin') {
        navigate('/projeto');
      } else {
        loadAnalyticsData();
      }
    }
  }, [user, profile, authLoading, navigate]);

  useEffect(() => {
    applyFilters();
  }, [calculations, dateRange, clientFilter, projectTypeFilter]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await profitCalculationService?.getAllCalculations();
      setCalculations(data || []);
    } catch (err) {
      setError('Erro ao carregar dados de análise: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...calculations];

    // Date range filter
    const now = new Date();
    if (dateRange === 'month') {
      const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      filtered = filtered?.filter(calc => new Date(calc?.createdAt) >= monthAgo);
    } else if (dateRange === 'quarter') {
      const quarterAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
      filtered = filtered?.filter(calc => new Date(calc?.createdAt) >= quarterAgo);
    } else if (dateRange === 'year') {
      const yearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      filtered = filtered?.filter(calc => new Date(calc?.createdAt) >= yearAgo);
    }

    // Client filter
    if (clientFilter !== 'all') {
      filtered = filtered?.filter(calc => calc?.userName === clientFilter);
    }

    setFilteredData(filtered);
  };

  const calculateMetrics = () => {
    const totalRevenue = filteredData?.reduce((sum, calc) => sum + (calc?.totalObra || 0), 0);
    const totalExpenses = filteredData?.reduce((sum, calc) => sum + (calc?.totalGastos || 0), 0);
    const totalProfit = totalRevenue - totalExpenses;
    const avgMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

    return {
      totalRevenue,
      totalExpenses,
      totalProfit,
      avgMargin,
      projectCount: filteredData?.length
    };
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erro ao sair:', error);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-sm sm:text-base text-muted-foreground">Carregando relatórios...</p>
        </div>
      </div>
    );
  }

  const metrics = calculateMetrics();

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader userName={profile?.fullName || profile?.email} onSignOut={handleSignOut} />
      
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Relatórios e Analytics</h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1 sm:mt-2">
              Análise de desempenho financeiro e tendências de lucro
            </p>
          </div>
          <button
            onClick={() => setShowExportPanel(!showExportPanel)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Download className="h-4 w-4" />
            Exportar Relatório
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Filter Controls */}
        <FilterControls
          dateRange={dateRange}
          setDateRange={setDateRange}
          clientFilter={clientFilter}
          setClientFilter={setClientFilter}
          projectTypeFilter={projectTypeFilter}
          setProjectTypeFilter={setProjectTypeFilter}
          calculations={calculations}
        />

        {/* Export Panel */}
        {showExportPanel && (
          <ExportPanel
            data={filteredData}
            metrics={metrics}
            onClose={() => setShowExportPanel(false)}
          />
        )}

        {/* KPI Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 mb-6">
          <MetricCard
            title="Receita Total"
            value={new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })?.format(metrics?.totalRevenue)}
            subtitle={`${metrics?.projectCount} projetos`}
            trend="up"
            Icon={TrendingUp}
          />
          <MetricCard
            title="Lucro Líquido"
            value={new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })?.format(metrics?.totalProfit)}
            subtitle="Resultado consolidado"
            trend={metrics?.totalProfit > 0 ? 'up' : 'down'}
            Icon={TrendingUp}
          />
          <MetricCard
            title="Margem Média"
            value={`${metrics?.avgMargin?.toFixed(1)}%`}
            subtitle="Lucratividade média"
            trend={metrics?.avgMargin > 0 ? 'up' : 'down'}
            Icon={TrendingUp}
          />
          <MetricCard
            title="Despesas Totais"
            value={new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })?.format(metrics?.totalExpenses)}
            subtitle="Custos operacionais"
            trend="neutral"
            Icon={FileText}
          />
        </div>

        {/* Performance Metrics */}
        <PerformanceMetrics data={filteredData} dateRange={dateRange} />

        {/* Profit Trend Chart */}
        <div className="mb-6">
          <ProfitTrendChart data={filteredData} dateRange={dateRange} />
        </div>

        {/* Top Profitable Projects */}
        <TopProjectsTable data={filteredData} />
      </div>
    </div>
  );
};

export default ReportsAndAnalyticsDashboard;